import { useEffect, useState } from 'react';
import type { ChatUiFlightSegment } from '@voll-intelligence/genui-contract';
import {
  onTimeStatLabel,
  soldAsLabel,
  withTerminal as kitWithTerminal,
} from '@voll-intelligence/intelligence-ui-kit';
import { useGenUiConfig } from './config.js';
import { flightDetailCopyOf } from './flight_detail_copy.js';
import { useGenUiI18n } from './i18n.js';

type TFn = ReturnType<typeof useGenUiI18n>['t'];

/** Enriquecimento Cirium de UM trecho direto — o mesmo shape dos campos por-perna
 *  do GET /flight/legs. Tudo opcional: a malha pode não cobrir o voo. */
export interface FlightSegmentEnrichment {
  aircraftName?: string;
  departureTerminal?: string;
  arrivalTerminal?: string;
  onTimePercent?: number;
  onTimeObservations?: number;
  soldAs?: string[];
}

/** Identidade do voo — a chave real do Cirium (nunca offerId, que é ref da sessão
 *  modal e expira). Tudo já está no wire `ChatUiFlightSegment`. */
export interface FlightSegmentEnrichmentQuery {
  carrier: string;
  flightNumber: string;
  departureDate: string;
  origin: string;
  destination: string;
}

/** Port de leitura do enriquecimento dos CARDS (GET /flight/segment-enrichment).
 *  Injetado via `GenUiConfig.ports.flightSegmentEnrichment`; ausente ⇒ drawer seco. */
export interface FlightSegmentEnrichmentPort {
  getSegmentEnrichment(q: FlightSegmentEnrichmentQuery): Promise<FlightSegmentEnrichment>;
}

/*
 * Os rótulos do enriquecimento moram no intelligence-ui-kit (#4997), que o
 * detalhe do voo do kit usa. Estas versões leem pelo tradutor do chat, para o
 * drawer dos cards (`SegmentDetailDrawer`) não mudar de import.
 */
export function enrichmentSoldAsLabel(soldAs: string[] | undefined, t: TFn): string | undefined {
  return soldAsLabel(soldAs, flightDetailCopyOf(t));
}

export function enrichmentStatsLabel(
  percent: number | undefined,
  observations: number | undefined,
  t: TFn,
): string | undefined {
  return onTimeStatLabel(percent, observations, flightDetailCopyOf(t));
}

export function withTerminal(
  name: string | undefined,
  terminal: string | undefined,
  t: TFn,
): string | undefined {
  return kitWithTerminal(name, terminal, flightDetailCopyOf(t));
}

export { hasEnrichmentData } from '@voll-intelligence/intelligence-ui-kit';

/**
 * Busca lazy do enriquecimento de UM trecho, no abrir do drawer. Só dispara com
 * port injetado, trecho DIRETO (as conexões do wire não carregam nº de voo por
 * perna, então enriquecer seria atribuir dado da 1ª perna ao trecho inteiro) e
 * identidade completa. Qualquer falha ⇒ null — drawer seco, fail-soft.
 */
export function useSegmentEnrichment(seg: ChatUiFlightSegment): FlightSegmentEnrichment | null {
  const port = useGenUiConfig().ports?.flightSegmentEnrichment ?? null;
  const direct = (seg.connections ?? []).length === 0;
  const { carrier, flightNumber, departAt, from, to } = seg;
  // A resposta carrega o pedido que respondeu: mesmo drawer, outro trecho (o card de
  // reserva só troca o `seg`), o dado do trecho anterior não sobrevive à troca.
  const key = JSON.stringify([direct, carrier, flightNumber, departAt, from, to]);
  const [answer, setAnswer] = useState<{
    port: typeof port;
    key: string;
    enrichment: FlightSegmentEnrichment | null;
  } | null>(null);

  useEffect(() => {
    if (!port || !direct || !carrier || !flightNumber || !departAt) return undefined;
    let cancelled = false;
    const answered = (enrichment: FlightSegmentEnrichment | null) => {
      if (!cancelled) setAnswer({ port, key, enrichment });
    };
    void port
      .getSegmentEnrichment({
        carrier,
        flightNumber,
        departureDate: departAt.slice(0, 10),
        origin: from,
        destination: to,
      })
      .then(answered)
      .catch(() => {
        answered(null);
      });
    return () => {
      cancelled = true;
    };
  }, [port, key, direct, carrier, flightNumber, departAt, from, to]);

  return answer?.port === port && answer.key === key ? answer.enrichment : null;
}
